import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { supabase } from '../supabaseClient';

const API = process.env.REACT_APP_API_URL || '';

export default function ForgotPassword() {
  const [identifier, setIdentifier] = useState('');
  const [loading, setLoading]       = useState(false);
  const [sent, setSent]             = useState(false);
  const [error, setError]           = useState('');
  const [cooldownMins, setCooldownMins] = useState(0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!identifier.trim()) return;
    setLoading(true);
    setError('');

    try {
      // Resolve username → email if needed
      let email = identifier.trim();
      if (!email.includes('@')) {
        const res = await axios.get(`${API}/auth/lookup-email`, { params: { username: email } });
        email = res.data.email;
      }
      // Supabase sends the reset link — no need to manage tokens ourselves
      const { error: sbError } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      if (sbError) throw new Error(sbError.message);
      setSent(true);
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-card" style={{ maxWidth: 420 }}>

        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <div style={{ fontSize: 26, fontWeight: 900, color: 'var(--cb-navy)', letterSpacing: '-0.5px' }}>
            Ranked<span style={{ color: 'var(--cb-blue)' }}>SAT</span>
          </div>
        </div>

        {sent ? (
          /* ── Success state ── */
          <div style={{ textAlign: 'center' }}>
            <div style={{
              width: 56, height: 56, borderRadius: '50%',
              background: 'var(--cb-green-light, #f0fdf4)',
              border: '2px solid var(--cb-green)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 16px',
            }}>
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none"
                stroke="var(--cb-green)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 6L9 17l-5-5"/>
              </svg>
            </div>
            <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--cb-navy)', marginBottom: 8 }}>
              Check your email
            </div>
            <p style={{ fontSize: 14, color: 'var(--cb-gray-500)', lineHeight: 1.6, marginBottom: 24 }}>
              If an account exists for <strong>{identifier.trim()}</strong>, we sent a
              password reset link. It expires in <strong>1 hour</strong>.
            </p>
            <p style={{ fontSize: 13, color: 'var(--cb-gray-400)', marginBottom: 20 }}>
              Didn't receive it? Check your spam folder, or{' '}
              <button
                className="link-btn"
                style={{ fontWeight: 600, color: 'var(--cb-blue)', background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontSize: 13 }}
                onClick={() => { setSent(false); setError(''); }}
              >
                try again
              </button>.
            </p>
            <Link to="/login" className="btn btn-outline" style={{ width: '100%', display: 'block', textAlign: 'center' }}>
              Back to Login
            </Link>
          </div>
        ) : (
          /* ── Request form ── */
          <>
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--cb-navy)', marginBottom: 6 }}>
                Forgot your password?
              </div>
              <p style={{ fontSize: 14, color: 'var(--cb-gray-500)', margin: 0, lineHeight: 1.5 }}>
                Enter your email or username and we'll send you a reset link.
              </p>
            </div>

            <form className="email-form" onSubmit={handleSubmit}>
              <div className="form-group">
                <label className="form-label">Email or username</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="Email or username"
                  value={identifier}
                  onChange={e => setIdentifier(e.target.value)}
                  autoFocus
                  required
                  autoComplete="username"
                />
              </div>

              {error && (
                <div className="form-error" style={{ marginBottom: 12 }}>{error}</div>
              )}

              {cooldownMins > 0 && !error && (
                <div style={{ fontSize: 12, color: 'var(--cb-gray-400)', marginBottom: 10, textAlign: 'center' }}>
                  You can request another link in {cooldownMins} minute{cooldownMins !== 1 ? 's' : ''}.
                </div>
              )}

              <button
                type="submit"
                className="btn btn-primary"
                style={{ width: '100%', marginTop: 4 }}
                disabled={loading || !identifier.trim()}
              >
                {loading ? 'Sending…' : 'Send Reset Link'}
              </button>
            </form>

            <div style={{ textAlign: 'center', marginTop: 20, fontSize: 14, color: 'var(--cb-gray-500)' }}>
              <Link to="/login" style={{ color: 'var(--cb-blue)', fontWeight: 600, textDecoration: 'none' }}>
                ← Back to Login
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
